import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { FCC } from '@eluve/components';
import {
  LoggerArgs,
  LoggingContextProvider,
  useExtendedLogger,
} from '@eluve/logger';
import { usePostHog } from '@eluve/posthog-react';
import {
  useImpersonation,
  useSession,
  useUserDisplayFromSession,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { usePersistentStorage } from '@eluve/utility-hooks';

export const UserContextProvider: FCC = ({ children }) => {
  const userId = useUserIdFromSession();
  const user = useUserDisplayFromSession();
  const postHog = usePostHog();
  const { requestPermission } = usePersistentStorage();
  const { isImpersonating } = useImpersonation();
  const session = useSession();

  const { pathname } = useLocation();

  useEffect(
    function setUserContextForServices() {
      postHog.identify(userId, {
        email: user.email,
      });

      if (isImpersonating && session.impersonation) {
        postHog.register({
          is_impersonating: true,
          impersonator_id: session.impersonation.impersonatorId,
        });
      } else {
        postHog.unregister('is_impersonating');
        postHog.unregister('impersonator_id');
      }

      Sentry.setUser({
        id: userId,
        email: user.email,
        impersonator_id: session.impersonation?.impersonatorId,
      });

      // Request persistent storage permission on first login
      if (userId) {
        requestPermission();
      }
    },
    [
      userId,
      user.email,
      postHog,
      requestPermission,
      isImpersonating,
      session.impersonation?.impersonatorId,
      session.impersonation,
    ],
  );

  useEffect(() => {
    postHog.capture('$pageview');
  }, [pathname, postHog]);

  const loggerArgs: LoggerArgs = useMemo(() => {
    return {
      name: 'UserScopedLogger',
      metadata: { userId },
    };
  }, [userId]);

  const userLogger = useExtendedLogger(loggerArgs);

  return (
    <LoggingContextProvider loggerInput={userLogger}>
      {children}
    </LoggingContextProvider>
  );
};
